/**
 * Frontend API client pra /api/v1/dashboard/* (R-IMPORT 3 / A3.2).
 *
 * Wrapper sobre `api.ts` que faz GET/PUT do layout + widgets catalog +
 * tema preferences.
 */
import { api } from '@/lib/api';
import { KNOWN_WIDGET_KEYS } from '@/widgets/renderer-map';

export interface Widget {
  id: string;
  key: string;
  nome: string;
  categoria: 'kpi' | 'chart' | 'lista' | 'acao' | 'fixo' | 'cliente';
  descricao: string | null;
  perfis_alvo_json: string;
  dept_alvo_json: string | null;
  mobile_ok: number;
  default_size: string | null;
  ordem: number;
  ativo: number;
}

export interface LayoutItem {
  i: string; // widget key
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  static?: boolean;
}

export interface Layouts {
  lg?: LayoutItem[];
  md?: LayoutItem[];
  sm?: LayoutItem[];
  xs?: LayoutItem[];
  xxs?: LayoutItem[];
}

export interface DashboardLayout {
  id: string;
  user_id: string;
  layout_versao: number;
  layout_json: string;
  ativo: number;
  criado_em_iso: string;
}

export interface GetLayoutResponse {
  layout: DashboardLayout | null;
  mode: 'saved' | 'default';
  /**
   * De qual camada veio (07/08/2026): `pessoa` = ajuste próprio, `perfil` =
   * padrão do time, `nenhum` = sem layout. É o que permite oferecer "restaurar
   * o padrão" só a quem tem ajuste próprio.
   */
  origem?: 'pessoa' | 'perfil' | 'nenhum';
}

export interface SaveLayoutResponse {
  layoutVersao: number;
  layoutId: string;
}

export type ModoSalvar = 'sobrescrever' | 'nova_versao';

export type ThemeMode =
  | 'manual_claro'
  | 'manual_escuro'
  | 'automatico'
  | 'sistema';

export interface TemaPreference {
  user_id: string;
  theme_mode: ThemeMode;
  theme_auto_inicio_hhmm: string | null;
  theme_auto_fim_hhmm: string | null;
  atualizado_em_iso: string;
}

/**
 * Catálogo de widgets oferecidos na Personalização da Home.
 *
 * FASE-C: o catálogo do banco (seed da migration 128) ainda lista widgets que
 * nunca ganharam implementação — apareciam na galeria com selo "beta" e o
 * aviso "aguarda implementação backend". A galeria passa a mostrar só o que
 * tem renderer de verdade (`KNOWN_WIDGET_KEYS`). Quando um widget for
 * implementado, basta registrá-lo no renderer-map que ele volta à galeria.
 */
export async function fetchWidgetsCatalog(): Promise<{
  items: Widget[];
  perfil: string;
}> {
  const resp = await api.get<{ items: Widget[]; perfil: string }>(
    '/api/v1/dashboard/widgets',
  );
  const disponiveis = new Set(KNOWN_WIDGET_KEYS);
  return { ...resp, items: (resp.items ?? []).filter((w) => disponiveis.has(w.key)) };
}

export async function fetchLayoutAtivo(): Promise<GetLayoutResponse> {
  return api.get<GetLayoutResponse>('/api/v1/dashboard/layout');
}

export async function fetchVersoesAnteriores(): Promise<{
  items: DashboardLayout[];
}> {
  return api.get<{ items: DashboardLayout[] }>('/api/v1/dashboard/layout/versoes');
}

export async function saveLayout(opts: {
  layouts: Layouts;
  modo: ModoSalvar;
}): Promise<SaveLayoutResponse> {
  return api.put<SaveLayoutResponse>('/api/v1/dashboard/layout', {
    layoutJson: JSON.stringify(opts.layouts),
    modo: opts.modo,
  });
}

/**
 * Salva o layout como PADRÃO de um perfil (07/08/2026) — a peça que faltava
 * para "montar a home daquele perfil". Admin-only no servidor.
 */
export async function salvarPadraoDoPerfil(opts: {
  perfilSlug: string;
  layouts: Layouts;
}): Promise<SaveLayoutResponse> {
  return api.put<SaveLayoutResponse>(
    `/api/v1/dashboard/layout/perfil/${encodeURIComponent(opts.perfilSlug)}`,
    { layoutJson: JSON.stringify(opts.layouts) },
  );
}

/** "Restaurar o padrão" (H-2): desfaz o ajuste da pessoa e volta ao do perfil. */
export async function restaurarPadraoDoPerfil(): Promise<{
  restaurado: boolean;
  origem: 'pessoa' | 'perfil' | 'nenhum';
}> {
  return api.post('/api/v1/dashboard/layout/restaurar-padrao', {});
}

export async function restaurarVersao(versaoAlvo: number): Promise<SaveLayoutResponse> {
  return api.post<SaveLayoutResponse>('/api/v1/dashboard/layout/restaurar', {
    versaoAlvo,
  });
}

export async function fetchTema(): Promise<TemaPreference> {
  return api.get<TemaPreference>('/api/v1/dashboard/tema');
}

export async function saveTema(opts: {
  themeMode: ThemeMode;
  inicioHhmm?: string | null;
  fimHhmm?: string | null;
}): Promise<void> {
  return api.put('/api/v1/dashboard/tema', opts);
}

/**
 * Builda Layouts default a partir dos widgets disponíveis pro user, usando
 * default_size de cada widget. Pra users sem layout salvo (primeiro acesso).
 */
export function buildDefaultLayouts(widgets: Widget[]): Layouts {
  const lg: LayoutItem[] = widgets.map((w) => {
    const def = w.default_size ? safeParse(w.default_size) : null;
    return {
      i: w.key,
      x: def?.x ?? 0,
      y: def?.y ?? 0,
      w: def?.w ?? 3,
      h: def?.h ?? 2,
      minW: 2,
      minH: 1,
    };
  });
  return { lg };
}

function safeParse(json: string): { x?: number; y?: number; w?: number; h?: number } | null {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
